import { JSX } from 'preact';
import { AUTHOR_VARS, useChorusProgress } from './_shared';

interface ChorusHeroProps {
  animate?: boolean;
  labels?: string[];
  compact?: boolean;
}

const DEFAULT_LABELS = ['Dad', 'Mom', 'Sam', 'Kai', 'Jo'];
const HERO_ORDER = [0, 1, 3, 2, 4];

export function ChorusHero({
  animate = true,
  labels = DEFAULT_LABELS,
  compact = false,
}: ChorusHeroProps): JSX.Element {
  const t = useChorusProgress(animate, 1400);
  const displayLabels = finalLabels(labels);

  const final = [
    { cx: 100, cy: 92, r: 62 },
    { cx: 196, cy: 72, r: 50 },
    { cx: 268, cy: 112, r: 55 },
    { cx: 148, cy: 152, r: 54 },
    { cx: 228, cy: 172, r: 46 },
  ];

  const cx0 = 177;
  const cy0 = 122;
  const getPos = (i: number, f: { cx: number; cy: number; r: number }) => {
    const ang = (i / 5) * Math.PI * 2 - Math.PI / 2;
    const sx = cx0 + Math.cos(ang) * 240;
    const sy = cy0 + Math.sin(ang) * 180;
    const x = sx + (f.cx - sx) * t;
    const y = sy + (f.cy - sy) * t;
    const r = f.r * (0.3 + 0.7 * t);
    return { x, y, r };
  };

  const H = compact ? 180 : 220;

  return (
    <div style={{ position: 'relative', height: H, width: '100%', overflow: 'visible' }}>
      <svg width="100%" height={H} viewBox={`0 0 354 ${H}`} style={{ position: 'absolute', inset: 0 }} aria-hidden="true">
        {final.map((b, i) => {
          const p = getPos(i, b);
          return (
            <circle
              key={`c-${i}`}
              cx={p.x}
              cy={p.y}
              r={p.r}
              fill={AUTHOR_VARS[HERO_ORDER[i]]}
              opacity={0.5 + 0.3 * t}
              style={{ mixBlendMode: 'multiply' }}
            />
          );
        })}
        {final.map((b, i) => {
          const p = getPos(i, b);
          return (
            <text
              key={`t-${i}`}
              x={p.x}
              y={p.y + 5}
              textAnchor="middle"
              fontFamily="var(--st-font-body)"
              fontSize="13"
              fontWeight="600"
              fill="#fff"
              opacity={Math.max(0, t * 2 - 1)}
            >
              {displayLabels[i]}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

/**
 * Labels for the invite-preview fallback illustration: the story's key-people
 * names (from the preview payload) followed by the invitee placeholder.
 * Returns undefined when there are no names so ChorusHero shows the fully
 * generic illustration instead of a half-personalized one.
 */
export function inviteIllustrationLabels(names: string[] | undefined): string[] | undefined {
  if (!names || names.length === 0) return undefined;
  // Cap the names so 'You' — the invitee-inclusion hook — always keeps a bubble.
  return [...names.slice(0, DEFAULT_LABELS.length - 1), 'You'];
}

// Longest label that fits the smallest bubble at 13px without spilling out.
const LABEL_MAX_CHARS = 12;

// Real names never mix with the generic defaults: a partial custom list leaves
// the remaining bubbles unlabeled (plain color blobs) rather than inventing
// people who aren't in the story.
function finalLabels(labels: string[]): string[] {
  const cleaned = labels
    .map(truncateLabel)
    .filter((label) => label.length > 0)
    .slice(0, DEFAULT_LABELS.length);

  if (cleaned.length === 0) return DEFAULT_LABELS;
  return DEFAULT_LABELS.map((_, index) => cleaned[index] ?? '');
}

function truncateLabel(label: string): string {
  const trimmed = label.trim();
  if (trimmed.length <= LABEL_MAX_CHARS) return trimmed;
  const firstWord = trimmed.split(/\s+/)[0] ?? '';
  // A very short first word ("Dr.", "J.") doesn't identify anyone — clamp the
  // whole label instead so the bubble still names a person.
  const alphaCount = (firstWord.match(/\p{L}/gu) ?? []).length;
  if (alphaCount >= 3 && firstWord.length <= LABEL_MAX_CHARS) return firstWord;
  return `${trimmed.slice(0, LABEL_MAX_CHARS - 1)}…`;
}
